import { useState, useEffect } from "react";
import {
  Theme,
  List,
  ListItem,
  ListItemText,
  useTheme,
  Box,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
} from "@mui/material";
import { IconButton } from "../IconButton/IconButton";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import * as MosaicActions from "src/redux/actions/Mosaic.Actions";
import { MosaicDirection } from "src/utils/MosaicTree";
import { useDispatch } from "react-redux";
import { IFullFlight } from "@vmosolutions/oc-interfaces";
import { API, Auth } from "aws-amplify";
import { ViewId } from "src/redux/reducers/MosaicManagerReducer";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LoadingWrapper from "../LoadingWrapper/LoadingWrapper";

interface ISummaryListProps {
  flightIds: string[];
  disabled?: boolean;
}

export interface ISummaryItem {
  key: string;
  value: string | number | JSX.Element;
  label?: string | JSX.Element;
}

const summaryTitleStyles = (theme: Theme): any => ({
  "&:selected": {
    backgroundColor: theme.custom.timeline.contextListBackground,
  },
});

const FlightSummaryList = (props: ISummaryListProps): JSX.Element => {
  const { flightIds, disabled } = props;
  const theme: Theme = useTheme();
  const [summaryItems, setSummaryItems] = useState<ISummaryItem[]>([]);
  const [flights, setFlights] = useState<IFullFlight[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const dispatch = useDispatch();

  const openWindow = (): void => {
    const windowTitle = `${flights.map((flight) => `#${flight.flightNumber}`).join(", ")}: Summary`;
    const summaryWindow: ViewId = "FlightSummary";
    const statusBarWidth = 450;
    const windowProps = {
      flights: flights,
    };
    dispatch(
      MosaicActions.openWindow(windowTitle, summaryWindow, [""], windowProps, "context", true, MosaicDirection.Right, statusBarWidth)
    );
  };

  useEffect(() => {
    setLoading(true);
    if (!disabled)
      retrieveAllFlightData(flightIds, setLoading)
        .then((data: IFullFlight[]) => setFlights(data))
        .catch((err) => console.log(err));
  }, [flightIds, disabled]);

  useEffect(() => {
    if (!disabled && flights) setSummaryItems(processData(flights) || []);
  }, [flights, disabled]);

  if (disabled)
    return (
      <ListItem sx={summaryTitleStyles(theme)} disabled>
        <ListItemText secondary="Summary Not Available" />
      </ListItem>
    );

  return (
    <Accordion defaultExpanded>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={"flight-summary-panelContent"}
        id={"flight-summary-panelHeader"}
      >
        <Grid container spacing={0} sx={{ display: "flex", alignItems: "center" }}>
          <Grid item xs="auto">
            Summary
          </Grid>
          <Grid item xs sx={{ justifyContent: "end", display: "flex" }}>
            <IconButton id="popout_summary_btn" onClick={openWindow} sx={{ p: 0, svg: { width: "16px !important" } }}>
              <OpenInNewIcon />
            </IconButton>
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <List component="div">
          {loading && <LoadingWrapper message={"loading..."} />}
          {!loading &&
            summaryItems.map((item) => (
              <ListItem
                component="div"
                key={`${item.key}-${flightIds.join("-")}`}
                sx={{ display: "flex", justifyContent: "space-between", py: 0, fontSize: "0.9em" }}
              >
                <Box sx={{ fontWeight: 900 }}>{item.label}</Box>
                <Box>{item.value}</Box>
              </ListItem>
            ))}
        </List>
      </AccordionDetails>
    </Accordion>
  );
};

const retrieveAllFlightData = async (flightIds: string[], setLoading: any): Promise<IFullFlight[]> => {
  const flightPromises = [];
  const flights: IFullFlight[] = [];
  for (const id of flightIds) {
    flightPromises.push(
      retrieveFlightData(id)
        .then((data: IFullFlight) => flights.push(data))
        .catch((err) => {
          console.log(err);
        })
    );
  }
  await Promise.all(flightPromises);
  setLoading(false);
  return flights;
};

const retrieveFlightData = async (id: string): Promise<IFullFlight> => {
  const userSession = await Auth.currentSession();
  const token = userSession.getIdToken().getJwtToken();
  const data: IFullFlight = await API.get("skytrains", `/flights/${id}`, {
    headers: {
      Accept: "*/*",
      Authorization: token,
    },
  });
  return data;
};

const processData = (flights: IFullFlight[]): ISummaryItem[] => {
  if (!flights || flights.length === 0) {
    return [];
  }
  const items: ISummaryItem[] = [];

  let allFlightsPaxCount = 0;
  for (const flight of flights) {
    if (!!flight.aircraft && !!flight.aircraftConfiguration) {
      const passengerCount =
        flight?.custom?.pxCount > 0
          ? flight?.custom?.pxCount
          : flight?.custom?.paxCount > 0
            ? flight?.custom?.paxCount
            : flight?.custom?.amelia_wrk_total_count > 0
              ? flight?.custom?.amelia_wrk_total_count
              : flight?.custom?.logipal?.totalPassengerCount > 0
                ? flight?.custom?.logipal?.totalPassengerCount
                : 0;
      allFlightsPaxCount += passengerCount;
    }
  }

  items.push({
    key: "paxCount",
    value: allFlightsPaxCount === 0 ? "N/A" : allFlightsPaxCount,
    label: "Passenger Count",
  });

  if (flights?.length === 1 && flights[0]?.custom?.alternate) {
    items.push({
      key: "alternate",
      value: flights[0]?.custom?.alternate,
      label: "Alternate",
    });
  }

  if (flights?.length === 1 && flights[0]?.custom?.plannedPayload?.value && flights[0]?.custom?.plannedPayload?.unit) {
    const value = flights[0]?.isReleased ? flights[0]?.custom?.plannedPayload?.value : "N/A";
    const payloadValue = `${value} ${flights[0]?.custom?.plannedPayload?.unit}`;
    items.push({
      key: "plannedPayload",
      value: payloadValue,
      label: "Payload",
    });
  }

  return items;
};

export default FlightSummaryList;
